import { useEffect } from "react";
import { useUser } from "../auth/useUser";
import { useRunStream } from "../hooks/useRunStream";
import { useGlobalRunStore, isTerminalRun } from "../hooks/useGlobalRunStore";
import { useProducts } from "../hooks/useProducts";

// ActiveRunProvider (0027 P1) is mounted once at the App root, OUTSIDE
// <Routes>, so the run streams it owns survive route navigation. It:
//   1. discovers in-flight runs from the product list's active_run_id (0159:
//      the run whose lease on the product is live, the one "is a run in
//      flight" answer; the latest run record's status is history and is not
//      read), on cold
//      mount (reload / fresh login) and on every refetch of the list. Each
//      named run goes through reviveRun, so an entry the client read as lost
//      or interrupted while its run was in fact alive (a heartbeat lapse, N6)
//      streams again as soon as the list shows the lease live (a window-focus
//      refetch or any mutation's invalidation); and
//   2. renders one RunStreamWorker per non-terminal run, each owning that
//      run's SSE stream and mirroring its state into the global store.
//
// The dedicated GET /me/active-runs endpoint (0027 scope) is deferred: the
// product list already carries active_run_id, so discovery needs no new
// backend. Revisit if per-product fan-out cost bites.

function RunStreamWorker({
  productId,
  runId,
  streamUrl,
}: {
  productId: string;
  runId?: string;
  streamUrl?: string;
}) {
  const stream = useRunStream({ productId, runId, streamUrl, enabled: true });
  const setSnapshot = useGlobalRunStore((s) => s.setSnapshot);
  useEffect(() => {
    setSnapshot(productId, stream);
  }, [stream, productId, setSnapshot]);
  return null;
}

export function ActiveRunProvider() {
  const { data: user } = useUser();
  const runs = useGlobalRunStore((s) => s.runs);
  const reviveRun = useGlobalRunStore((s) => s.reviveRun);

  // useProducts is the dashboard's list read too, so this adds no extra fetch
  // there; on other pages it's one cheap call used only for discovery.
  const productsQ = useProducts();

  useEffect(() => {
    const items = productsQ.data?.products ?? [];
    for (const p of items) {
      if (p.active_run_id) {
        reviveRun(p.id, p.active_run_id);
      }
    }
  }, [productsQ.data, reviveRun]);

  if (!user) return null;
  return (
    <>
      {Object.values(runs)
        .filter((r) => !isTerminalRun(r.snapshot))
        .map((r) => (
          <RunStreamWorker
            key={r.productId}
            productId={r.productId}
            runId={r.runId}
            streamUrl={r.streamUrl}
          />
        ))}
    </>
  );
}
